import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GeoPoint } from 'firebase/firestore';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as React from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMapEvents } from 'react-leaflet';

import useTingStore from '../../stores/tingStore';

// Fix for marker icon not showing up
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const LocationMarker = ({ onPick }) => {
    useMapEvents({
        click(e) {
            onPick(e.latlng);
        },
    });

    return null;
};

const callProcessLocation = async (latitude, longitude) => {
    const response = await fetch('https://us-central1-latestsightingsbackoffice.cloudfunctions.net/processLocation', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ latitude, longitude })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
    }

    return await response.json();
};

const getPrimaryCoordinates = (tingDoc) => {
    const latitude = tingDoc?.coordinates?.latitude;
    const longitude = tingDoc?.coordinates?.longitude;

    return {
        lat: Number.isFinite(latitude) ? latitude : 0,
        lng: Number.isFinite(longitude) ? longitude : 0
    };
};

export default function TingLocationDetails() {
    const tingDoc = useTingStore(state => state.tingDoc);
    const setTingDoc = useTingStore(state => state.setTingDoc);
    const [coords, setCoords] = React.useState([getPrimaryCoordinates(tingDoc)]);
    const [roadName, setRoadName] = React.useState('');
    const [locationDescription, setLocationDescription] = React.useState('');
    const [processingLocation, setProcessingLocation] = React.useState(false);
    const [locationError, setLocationError] = React.useState('');
    const primaryLatitude = Number.isFinite(tingDoc?.coordinates?.latitude)
        ? tingDoc.coordinates.latitude
        : 0;
    const primaryLongitude = Number.isFinite(tingDoc?.coordinates?.longitude)
        ? tingDoc.coordinates.longitude
        : 0;

    React.useEffect(() => {
        const primary = {
            lat: primaryLatitude,
            lng: primaryLongitude
        };
        setCoords((currentCoords) => (
            currentCoords.length === 2 ? [primary, currentCoords[1]] : [primary]
        ));
    }, [primaryLatitude, primaryLongitude]);

    const setPrimaryCoordinatesInDoc = (latitude, longitude) => {
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

        setTingDoc({
            ...tingDoc,
            coordinates: new GeoPoint(latitude, longitude)
        });
    };

    const handleCoordChange = (event) => {
        const numericValue = Number.parseFloat(event.target.value);
        if (!Number.isFinite(numericValue)) return;

        const primaryCoords = coords[0] ?? { lat: 0, lng: 0 };
        const updatedPrimaryCoords = event.target.id === 'lat'
            ? { ...primaryCoords, lat: numericValue }
            : { ...primaryCoords, lng: numericValue };
        const updatedCoords = coords.length === 2
            ? [updatedPrimaryCoords, coords[1]]
            : [updatedPrimaryCoords];

        setCoords(updatedCoords);
        setPrimaryCoordinatesInDoc(updatedPrimaryCoords.lat, updatedPrimaryCoords.lng);
    };


    const handleAddCoord = () => {
        if (coords.length < 2) {
            setCoords([...coords, { lat: coords[0]?.lat ?? 0, lng: coords[0]?.lng ?? 0 }]);
        }
    };

    const handleCoordUpdate = (index, coord) => {
        if (!Number.isFinite(coord?.lat) || !Number.isFinite(coord?.lng)) return;

        const newCoords = [...coords];
        newCoords[index] = coord;
        setCoords(newCoords);

        if (index === 0) {
            setPrimaryCoordinatesInDoc(coord.lat, coord.lng);
        }
    };

    const fetchLocationDetails = async (coord) => {
        if (!Number.isFinite(coord?.lat) || !Number.isFinite(coord?.lng)) return;

        try {
            setLocationError('');
            setProcessingLocation(true);
            const result = await callProcessLocation(coord.lat, coord.lng);

            setRoadName(result?.roadname ?? '');
            setLocationDescription(result?.locationDescription ?? '');
        } catch (error) {
            console.error('Error fetching road name:', error);
            setLocationError('Could not process this coordinate right now. Please try again.');
        } finally {
            setProcessingLocation(false);
        }
    };

    const appendToLocationDescription = () => {
        if (!locationDescription) return;

        setTingDoc({
            ...tingDoc,
            locationDescription
        });
    };

    const mapCenter = [coords[0]?.lat || 0, coords[0]?.lng || 0];

    return (
        <>
            <Box component="form" className="ting-editor__form">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            id="lat"
                            label="Latitude"
                            value={coords[0]?.lat ?? 0}
                            onChange={handleCoordChange}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            id="lng"
                            label="Longitude"
                            value={coords[0]?.lng ?? 0}
                            onChange={handleCoordChange}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            id="accuracy"
                            label="Map accuracy"
                            value={tingDoc?.accuracy ?? ''}
                            onChange={(event) => setTingDoc({ ...tingDoc, accuracy: event.target.value })}
                            size="small"
                            fullWidth
                        />
                    </Grid>

                    {coords.length === 2 && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="lat2"
                                    label="Latitude 2"
                                    value={coords[1].lat}
                                    onChange={(event) => {
                                        const numericValue = Number.parseFloat(event.target.value);
                                        if (!Number.isFinite(numericValue)) return;
                                        handleCoordUpdate(1, { ...coords[1], lat: numericValue });
                                    }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="lng2"
                                    label="Longitude 2"
                                    value={coords[1].lng}
                                    onChange={(event) => {
                                        const numericValue = Number.parseFloat(event.target.value);
                                        if (!Number.isFinite(numericValue)) return;
                                        handleCoordUpdate(1, { ...coords[1], lng: numericValue });
                                    }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <div className="ting-editor__action-row">
                            <Button
                                variant="contained"
                                onClick={() => fetchLocationDetails(coords[0])}
                                disabled={processingLocation}
                            >
                                {processingLocation ? 'Processing...' : 'Process Coordinates'}
                            </Button>
                            {coords.length < 2 && (
                                <Button variant="outlined" onClick={handleAddCoord}>
                                    Add Second Point
                                </Button>
                            )}
                        </div>
                        {processingLocation && (
                            <Typography variant="body2" className="ting-editor__status">
                                Processing location details...
                            </Typography>
                        )}
                        {locationError && (
                            <Typography variant="body2" className="ting-editor__status ting-editor__status--error">
                                {locationError}
                            </Typography>
                        )}
                    </Grid>

                    {locationDescription && (
                        <Grid item xs={12}>
                            <div className="ting-editor__location-preview">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {roadName || 'Suggested road'}
                                </Typography>
                                <Typography variant="body2">
                                    {locationDescription}
                                </Typography>
                                <div>
                                    <Button variant="outlined" size="small" onClick={appendToLocationDescription}>
                                        Use in Location Description
                                    </Button>
                                </div>
                            </div>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <div className="ting-editor__map-shell">
                <MapContainer center={mapCenter} zoom={13}>
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {coords.map((coord, index) => (
                        <Marker
                            key={index}
                            position={[coord.lat, coord.lng]}
                            draggable
                            eventHandlers={{
                                dragend: (event) => {
                                    const marker = event.target;
                                    const newPosition = marker.getLatLng();
                                    handleCoordUpdate(index, { lat: newPosition.lat, lng: newPosition.lng });
                                }
                            }}
                        />
                    ))}
                    {coords.length === 2 && (
                        <Polyline positions={coords.map((coord) => [coord.lat, coord.lng])} />
                    )}
                    <LocationMarker onPick={(coord) => handleCoordUpdate(coords.length - 1, coord)} />
                </MapContainer>
            </div>
        </>
    );
}
